import { ContentRow, Metadata } from "@dropbox/dash-component-library";
import { Text } from "@dropbox/dig-components/typography";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Open_DashLink } from "@mirage/analytics/events/types/open_dash_link";
import { activityDerivePAPProps } from "@mirage/service-feed/util/analytics";
import { getFirstActivity } from "@mirage/service-feed/util/helpers";
import { publishEvent, SurveyEvent } from "@mirage/service-feedback";
import { openURL } from "@mirage/service-platform-actions";
import { FileContentIconWithThumbnail } from "@mirage/shared/icons/FileContentIcon";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import { getTimeAgoString } from "@mirage/shared/util/time";
import { useCallback } from "react";
import { ActivityActionBar } from "../ActivityActionBar";
import { type ActivityObjectProps } from "../ActivityObject";

import type { dash_feed } from "@dropbox/api-v2-client";
import type { SearchResult } from "@mirage/shared/search/search-result";
import { ActivityLabel } from "../ActivityLabel/ActivityLabel";

type DocumentActivityObjectProps = {
  document: SearchResult;
} & ActivityObjectProps;

export const DocumentActivityObject = ({
  activity,
  index,
  document,
  currentAccount,
}: DocumentActivityObjectProps) => {
  const { reportPapEvent } = useMirageAnalyticsContext();
  const isMobileSize = useIsMobileSize();
  const onClickRow = useCallback(() => {
    reportPapEvent(
      PAP_Open_DashLink({
        ...activityDerivePAPProps(activity, index),
        actionSurfaceComponent: "activity_feed",
        featureLine: "activity_feed",
      }),
    );
    publishEvent(SurveyEvent.LaunchStartPageActivityItem, {}, true);
    openURL(document.url!);
  }, [document.url, reportPapEvent, activity, index]);

  return (
    <ContentRow
      isMobile={isMobileSize}
      paddingSize="medium"
      onClick={onClickRow}
      visibleUrl={document.url!}
      withStartAccessory={
        <DocumentActivityObjectIcon activity={activity} document={document} />
      }
      withLabel={<Text variant="label">{document.title}</Text>}
      withMeta={
        <DocumentActivityObjectMetadata
          activity={activity}
          document={document}
          currentAccount={currentAccount}
        />
      }
      withActions={<ActivityActionBar activity={activity} index={index} />}
    />
  );
};

const DocumentActivityObjectMetadata = ({
  document,
  activity,
  currentAccount,
}: DocumentActivityObjectProps) => {
  const timestampMs = getDocumentActivityTimestampMs(document, activity);
  return (
    <Metadata size="small">
      <Metadata.Item>
        <ActivityLabel currentAccount={currentAccount} activity={activity} />
      </Metadata.Item>
      <Metadata.Item>
        <Metadata.Label>{getTimeAgoString(timestampMs)}</Metadata.Label>
      </Metadata.Item>
    </Metadata>
  );
};

const getDocumentActivityTimestampMs = (
  document: SearchResult,
  activity: dash_feed.FeedItem,
): number => {
  const activityTs = getFirstActivity(activity)?.ts;
  if (activityTs) {
    return activityTs * 1000;
  }
  return document.providerUpdateAtMs!;
};

const DocumentActivityObjectIcon = ({
  document,
}: DocumentActivityObjectProps) => {
  return (
    <FileContentIconWithThumbnail
      size="large"
      content={document}
      shouldFetchThumbnail
    />
  );
};
